import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Divider,
    InputAdornment,
    CircularProgress,
    Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import TaskIcon from '@mui/icons-material/Task';
import FolderIcon from '@mui/icons-material/Folder';
import httpProvider from '../provider/HttpProvider';

const GlobalSearch = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [bookmarks, setBookmarks] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [allProjects, setAllProjects] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const navigate = useNavigate();

    const handleOpen = useCallback(() => {
        setOpen(true);
        setQuery('');
        setBookmarks([]);
        setTasks([]);
        setProjects([]);
        // Projects have no search endpoint of their own — fetch the
        // (small) non-archived list once per dialog open and filter
        // client-side alongside the debounced bookmark/task queries.
        httpProvider
            .get('/api/projects/?limit=200&is_archived=false')
            .then(res => setAllProjects(res.data?.rows ?? []))
            .catch(() => setAllProjects([]));
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                handleOpen();
            }
            if (e.key === 'Escape') {
                handleClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleOpen, handleClose]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        if (!query.trim()) {
            setBookmarks([]);
            setTasks([]);
            setProjects([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const [bmRes, taskRes] = await Promise.all([
                    httpProvider.get(`/api/bookmarks/search?term=${encodeURIComponent(query)}&limit=5`),
                    httpProvider.get(`/api/tasks/?limit=5&sort=id&sortDesc=true&term=${encodeURIComponent(query)}`),
                ]);
                setBookmarks(bmRes.data?.rows ?? []);
                setTasks(taskRes.data?.rows ?? []);

                const term = query.trim().toLowerCase();
                setProjects(
                    allProjects
                        .filter(p => p.name?.toLowerCase().includes(term))
                        .slice(0, 5)
                );
            } catch {
                // silently ignore search errors
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query, allProjects]);

    const handleBookmarkClick = hash => {
        navigate(`/bookmarks/${hash}`);
        handleClose();
    };

    const handleTaskClick = id => {
        navigate(`/tasks/${id}`);
        handleClose();
    };

    const handleProjectClick = id => {
        navigate(`/projects/${id}`);
        handleClose();
    };

    const hasResults = bookmarks.length > 0 || tasks.length > 0 || projects.length > 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { mt: '10vh', verticalAlign: 'top' } }}
        >
            <DialogContent sx={{ p: 0 }}>
                <TextField
                    autoFocus
                    fullWidth
                    placeholder="Search bookmarks and tasks… (Cmd+K)"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {loading ? <CircularProgress size={18} /> : <SearchIcon />}
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 0, border: 'none' },
                    }}
                    sx={{ '& fieldset': { border: 'none' } }}
                />

                {query && !loading && !hasResults && (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No results for &ldquo;{query}&rdquo;
                        </Typography>
                    </Box>
                )}

                {bookmarks.length > 0 && (
                    <>
                        <Divider />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', opacity: 0.6 }}>
                            Bookmarks
                        </Typography>
                        <List dense disablePadding>
                            {bookmarks.map(b => (
                                <ListItem
                                    key={b.hash}
                                    button
                                    onClick={() => handleBookmarkClick(b.hash)}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <BookmarkIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={b.title}
                                        secondary={b.url}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {tasks.length > 0 && (
                    <>
                        <Divider />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', opacity: 0.6 }}>
                            Tasks
                        </Typography>
                        <List dense disablePadding>
                            {tasks.map(t => (
                                <ListItem
                                    key={t.id}
                                    button
                                    onClick={() => handleTaskClick(t.id)}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <TaskIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t.title}
                                        secondary={t.description}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {projects.length > 0 && (
                    <>
                        <Divider />
                        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', opacity: 0.6 }}>
                            Projects
                        </Typography>
                        <List dense disablePadding>
                            {projects.map(p => (
                                <ListItem
                                    key={p.id}
                                    button
                                    onClick={() => handleProjectClick(p.id)}
                                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <FolderIcon fontSize="small" sx={{ color: p.color || undefined }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={p.name}
                                        secondary={p.description}
                                        primaryTypographyProps={{ noWrap: true }}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default GlobalSearch;
